import {
  FC,
  memo,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/Modal';
import { Portal } from 'components/Shared/Portal';
import { useHooryWidgetHiderCount } from 'hooks/third-party/useHooryWidget';
import { usePageNoScroll } from 'hooks/usePageNoScroll';
import * as Styled from './styles';
import { isMobile } from 'utils';
import { EGlobalClassnames } from 'constants/dom/global-classnames';

// TODO: group title props, description props, button props, image props
type ConfirmModalProps = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  image?: string;
  imageSizes?: [number, number];
  visible: boolean;
  onCancel: () => void;
  onClose?: () => void;
  onSubmit?: () => void;
  loading?: {
    submit?: boolean;
    cancel?: boolean;
  };
  disabled?: {
    submit?: boolean;
    cancel?: boolean;
  };
  cancelWithButton?: boolean;
  closable?: boolean;
  submitAsCancel?: boolean;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  children?: ReactNode;
  coloredTitle?: boolean;
  blockButtons?: boolean;
  className?: string;
  isLargeWidth?: boolean;
  titleColor?: string;
};

export const ConfirmModal = memo(
  ({
    className,
    title,
    description,
    confirmText,
    image,
    imageSizes = [110, 110],
    visible,
    onCancel,
    onClose: _onClose,
    onSubmit,
    loading = { cancel: false, submit: false },
    disabled = { cancel: false, submit: false },
    cancelText,
    cancelWithButton = false,
    closable = true,
    submitAsCancel = false,
    showCancelButton = true,
    showConfirmButton = true,
    blockButtons = false,
    children,
    coloredTitle = false,
    isLargeWidth = false,
    titleColor
  }: ConfirmModalProps) => {
    const { t } = useTranslation();
    const [animation, setAnimation] = useState('open');
    const [display, setDisplay] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const isFirstRender = useRef(true);
    const onClose = _onClose || onCancel;

    const hide = async () => {
      if (isMobile()) {
        setAnimation('close');
        clearTimeout(timeoutRef.current);
        await new Promise(r => (timeoutRef.current = setTimeout(r, 300)));
        setDisplay(false);
        onClose();
      } else {
        setDisplay(false);
        await new Promise(r => (timeoutRef.current = setTimeout(r, 200)));
        onClose();
      }
    };

    useEffect(() => {
      if (isFirstRender.current && !visible) {
        isFirstRender.current = false;
        return;
      }
      if (!visible) {
        hide();
      } else {
        clearTimeout(timeoutRef.current);
        setDisplay(true);
        setAnimation('open');
      }
      isFirstRender.current = false;
    }, [visible]);

    const BodyContent = (
      <Styled.TextCol>
        {image && (
          <Styled.ImageWrapper>
            <Styled.Image
              preview={false}
              src={image}
              style={{
                objectFit: 'cover'
              }}
              width={imageSizes?.[0]}
              height={imageSizes?.[1]}
            />
          </Styled.ImageWrapper>
        )}
        <Styled.Title $colored={coloredTitle} $color={titleColor}>
          {title}
        </Styled.Title>
        {description && (
          <Styled.Text
            dangerouslySetInnerHTML={{
              __html: description
            }}
          />
        )}
        {children}
        {(showCancelButton || showConfirmButton) && (
          <Styled.ButtonWrapper>
            {!!(showCancelButton && (cancelWithButton || _onClose)) && (
              <Styled.ButtonContainer>
                <Styled.ConfirmationButton
                  block={isMobile() || blockButtons}
                  type="secondary"
                  onClick={_onClose ? onCancel : hide}
                  loading={loading?.cancel}
                  disabled={disabled?.cancel}
                  btnheight={56}
                >
                  {cancelText || t('generic.no')}
                </Styled.ConfirmationButton>
              </Styled.ButtonContainer>
            )}
            {showConfirmButton && (
              <Styled.ButtonContainer>
                <Styled.ConfirmationButton
                  block={isMobile() || blockButtons}
                  type="primary"
                  onClick={submitAsCancel ? hide : onSubmit}
                  btnheight={56}
                  disabled={disabled?.submit}
                  loading={loading?.submit}
                >
                  {confirmText || t('generic.yes')}
                </Styled.ConfirmationButton>
              </Styled.ButtonContainer>
            )}
          </Styled.ButtonWrapper>
        )}
      </Styled.TextCol>
    );

    return display ? (
      !isMobile() ? (
        <Modal
          isOpen={display}
          onClose={hide}
          closeIconSize="small"
          closable={closable}
        >
          <Styled.Wrapper
            className={className}
            $withCloseIcon={closable && !cancelWithButton}
            $isLargeWidth={isLargeWidth}
          >
            {BodyContent}
          </Styled.Wrapper>
        </Modal>
      ) : (
        <Portal>
          <Popup
            isOpen={visible}
            cancelWithButton={cancelWithButton}
            animation={animation}
            hide={hide}
          >
            {BodyContent}
          </Popup>
        </Portal>
      )
    ) : null;
  }
);

// Consider refactoring this to have functionality separated
export const Popup: FC<{
  children: ReactElement;
  isOpen: boolean;
  animation: string;
  hide: () => void;
  cancelWithButton: ConfirmModalProps['cancelWithButton'];
}> = memo(({ children, isOpen, cancelWithButton, animation, hide }) => {
  usePageNoScroll(isOpen);
  useHooryWidgetHiderCount({ hiderType: 'modal', open: isOpen });

  return (
    <Styled.Popup
      className={`${animation} ${EGlobalClassnames.ConfirmPopup}`}
      onClick={e => e.stopPropagation()}
    >
      <Styled.PopupBody $withCloseIcon={!cancelWithButton}>
        <Styled.PopupCloseIcon onClick={hide} />
        {children}
      </Styled.PopupBody>
    </Styled.Popup>
  );
});
